import { SearchQueryParameterController } from "@/hooks/useSearchQueryParameter";
import { FC, MouseEventHandler, PropsWithChildren, useMemo } from "react";
import Link from "next/link";
import useFindEntityWithSubEntities, {
  Entity,
} from "@/hooks/useFindEntityWithSubEntitites";
import { theme } from "tailwind.config";

interface Props<T, P> {
  entities: T[];
  watchQueryParameter: string;
  query;
  queryController: SearchQueryParameterController<P>;
  onReachedBottomEntity?: () => void;
}

type EntityWithName<T> = Entity<T> & {
  name: string;
};

const EntityDrillDown = <T extends EntityWithName<T>, P>(
  props: PropsWithChildren<Props<T, P>>,
): JSX.Element => {
  const {
    entities,
    watchQueryParameter,
    queryController,
    query,
    onReachedBottomEntity,
  } = props;

  const { selectedEntity, parentEntities } = useFindEntityWithSubEntities<T>(
    entities,
    query[watchQueryParameter],
  );

  const selectedEntityParent = useMemo<T | null>(() => {
    if (!selectedEntity) return null;

    if (selectedEntity && selectedEntity.ancestry.length === 0) return null;

    return parentEntities[parentEntities.length - 1];
  }, [selectedEntity, parentEntities]);

  // 表示するエンティティ
  const displayEntities = useMemo<T[]>(() => {
    // 選択しているビルティングタイプがない => すべてを表示する
    if (!selectedEntity) return entities;

    // 子供がない => 最下層のEntity
    // => 一つ上のカテゴリを表示する
    if (!selectedEntity.children)
      return parentEntities[parentEntities.length - 1].children;

    return selectedEntity.children;
  }, [entities, selectedEntity, parentEntities]);

  // 現在選択されているEntityが最下層かどうかを判定する
  const reachedBottomEntity = useMemo<boolean>(() => {
    if (!selectedEntity) return false;

    if (selectedEntity.children) {
      return false;
    }

    return true;
  }, [selectedEntity]);

  // 「すべて」を選んだ時に対応するEntityを特定する
  const entityForAll = useMemo<T | null>(() => {
    // 最上層のEntityのとき
    if (!selectedEntity) {
      return null;
    }

    // 最下層のEntityのとき
    if (reachedBottomEntity) {
      return selectedEntityParent || selectedEntity;
    }

    return selectedEntity;
  }, [selectedEntity, selectedEntityParent, reachedBottomEntity]);

  // 「すべて」を選んだときに遷移するためのクエリ
  const allQuery = useMemo<P>(() => {
    const query = {};
    query[watchQueryParameter] = entityForAll
      ? entityForAll.id.toString()
      : null;

    return query as P;
  }, [entityForAll, watchQueryParameter]);

  // 現在「すべて」を選んでいるかどうかを判定する
  const selectedAll = useMemo<boolean>(() => {
    if (!selectedEntity) return false;

    return allQuery[watchQueryParameter] === selectedEntity.id.toString();
  }, [allQuery, watchQueryParameter, selectedEntity]);

  // 「戻る」を選択したときに対応するEntityを特定する
  const entityForBack = useMemo<T | null>(() => {
    // 一番上のEntityとき（戻るボタン自体がない）
    if (!selectedEntity) {
      return null;
    }

    // 一番下のEntityのとき
    if (reachedBottomEntity) {
      // 一番下のEntityが選択されている = 親のEntityのchildrenが表示されている
      // その状態で「戻る」を押したときに表示するEntityは、2つまえの親のEntityのchildrenとなる
      if (parentEntities.length < 2) {
        return null;
      } else {
        const grandParentEntity = parentEntities[parentEntities.length - 2];

        return grandParentEntity;
      }
    }

    return selectedEntityParent;
  }, [
    selectedEntity,
    selectedEntityParent,
    parentEntities,
    reachedBottomEntity,
  ]);

  const backQuery = useMemo<P>(() => {
    const query = {};
    query[watchQueryParameter] = entityForBack
      ? entityForBack.id.toString()
      : null;

    return query as P;
  }, [entityForBack, watchQueryParameter]);

  return (
    <ul className="BorderList text-sm bg-white">
      {selectedEntity && (
        <li>
          <Link
            href={queryController.toPath(backQuery)}
            className="flex items-center space-x-8 p-8"
          >
            <i className="icon-Arrow-left"></i>
            <span>戻る</span>
          </Link>
        </li>
      )}

      {entityForAll && (
        <li>
          <DrillDownCell
            href={queryController.toPath(allQuery as P)}
            name={`すべて(${entityForAll.name})`}
            hasArrow={false}
            selected={selectedAll}
          />
        </li>
      )}

      {displayEntities.map((e) => {
        const selected = selectedEntity ? e.id === selectedEntity.id : false;

        const query = {};
        query[watchQueryParameter] = e.id.toString();

        return (
          <li key={e.id}>
            <DrillDownCell
              href={queryController.toPath(query as P)}
              name={e.name}
              hasArrow={!!e.children}
              selected={selected}
              onClick={() => {
                if (!e.children) {
                  if (onReachedBottomEntity) onReachedBottomEntity();
                }
              }}
            />
          </li>
        );
      })}
    </ul>
  );
};
export default EntityDrillDown;

type DrillDownCellProp = {
  href: string;
  selected: boolean;
  name: string;
  hasArrow: boolean;
  onClick?: MouseEventHandler;
};

export const DrillDownCell: FC<DrillDownCellProp> = ({
  name,
  hasArrow,
  href,
  selected,
  onClick,
}): JSX.Element => {
  return (
    <Link
      href={href}
      className="flex items-center justify-between p-12"
      style={{
        backgroundColor: selected ? theme.colors.white : "",
        color: selected ? theme.colors.gray[950] : "",
      }}
      onClick={onClick}
    >
      <span>{name}</span>
      {hasArrow && <i className="icon-Arrow-right text-lg"></i>}
    </Link>
  );
};
